import React, { useState, useRef } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/Drones.css";
import { exportToCSV } from "../utils/exportUtils";

// GraphQL Definitions
const GET_HIRE_PILOTS = gql`
  query {
    hirePilots {
      pilotId
      pilotName
      pilotCompany
      location
      availability
      specification
      description
      adminStatus
      newemail
      newphoneNumber
      price {
        perHour
        perDay
      }
      certifications {
        url
      }
      resume {
        url
      }
      sellerId
      seller {
        name
        email
        phoneNumber
      }
    }
  }
`;

const UPDATE_HIRE_PILOT_STATUS = gql`
  mutation AdminUpdateHirePilotStatus($pilotId: String!, $adminStatus: String!) {
    adminUpdateHirePilotStatus(pilotId: $pilotId, adminStatus: $adminStatus) {
      pilotId
      adminStatus
    }
  }
`;

const DELETE_HIRE_PILOT = gql`
  mutation DeleteHirePilot($pilotId: String!) {
    deleteHirePilot(pilotId: $pilotId)
  }
`;

function HirePilotsDashboard() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sellerId", direction: "asc" });
  const [viewingPilot, setViewingPilot] = useState(null);
  const [viewingContent, setViewingContent] = useState({ type: null, content: null, title: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const tableRef = useRef(null);

  // Constants for pagination
  const ITEMS_PER_PAGE = 10;
  const PRODUCTS_PER_PAGE = 10;

  // Apollo Hooks
  const { loading, error: queryError, data, refetch } = useQuery(GET_HIRE_PILOTS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [updateStatusMutation] = useMutation(UPDATE_HIRE_PILOT_STATUS);
  const [deletePilotMutation] = useMutation(DELETE_HIRE_PILOT);

  const pilots = data?.hirePilots || [];
  const error = queryError ? queryError.message : null;

  // Update pilot status
  const updatePilotStatus = async (pilotId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${pilotId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({
        variables: { pilotId, adminStatus: newStatus }
      });
      refetch();
    } catch (err) {
      alert(`❌ Failed to update status: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${pilotId}-${newStatus}`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  // Delete pilot
  const deletePilot = async (pilotId) => {
    if (!window.confirm("Are you sure you want to delete this pilot?")) return;

    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${pilotId}-deleting`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await deletePilotMutation({
        variables: { pilotId }
      });
      refetch();
      if (viewingPilot && viewingPilot.pilotId === pilotId) {
        setViewingPilot(null);
      }
    } catch (err) {
      alert(`❌ Failed to delete pilot: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${pilotId}-deleting`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  // View content (specification, description, etc.)
  const viewContent = (type, content, title) => {
    setViewingContent({ type, content, title });
  };

  // Close content modal
  const closeContent = () => {
    setViewingContent({ type: null, content: null, title: null });
  };

  // Group pilots by seller ID
  const groupedBySeller = pilots.reduce((acc, pilot) => {
    const sellerId = pilot.sellerId;
    if (!acc[sellerId]) {
      acc[sellerId] = {
        sellerId: sellerId,
        sellerInfo: pilot.seller,
        pilots: []
      };
    }
    acc[sellerId].pilots.push(pilot);
    return acc;
  }, {});

  // Convert to array and sort
  const sellerGroups = Object.values(groupedBySeller).sort((a, b) =>
    a.sellerId.localeCompare(b.sellerId)
  );

  // Calculate seller stats
  const sellerGroupsWithStats = sellerGroups.map(seller => {
    const totalPilots = seller.pilots.length;
    const approvedPilots = seller.pilots.filter(p => p.adminStatus === 'approved').length;
    const pendingPilots = seller.pilots.filter(p => !p.adminStatus || p.adminStatus === 'pending').length;
    const rejectedPilots = seller.pilots.filter(p => p.adminStatus === 'rejected').length;
    const deactivatedPilots = seller.pilots.filter(p => p.adminStatus === 'Temporarily_Deactivated').length;
   
    return {
      ...seller,
      totalPilots,
      approvedPilots,
      pendingPilots,
      rejectedPilots,
      deactivatedPilots
    };
  });

  // Filter sellers based on search
  const filteredSellers = sellerGroupsWithStats.filter(seller => {
    const matchesSearch =
      searchTerm === "" ||
      seller.sellerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.phoneNumber?.includes(searchTerm) ||
      seller.pilots.some(pilot =>
        pilot.pilotName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilot.pilotCompany?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilot.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
   
    if (selectedStatus === "all") return matchesSearch;
   
    // Filter by status
    const filteredPilots = seller.pilots.filter(pilot => {
      const status = pilot.adminStatus?.toLowerCase() || "pending";
      return status === selectedStatus;
    });
   
    return matchesSearch && filteredPilots.length > 0;
  });

  // Sort sellers
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'sellerId' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination for sellers
  const totalFilteredPages = Math.ceil(sortedSellers.length / ITEMS_PER_PAGE);
  const paginatedSellers = sortedSellers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get filtered pilots for expanded seller with pagination
  const getFilteredPilotsForSeller = (sellerId) => {
    const seller = groupedBySeller[sellerId];
    if (!seller) return [];
   
    return seller.pilots.filter(pilot => {
      const status = pilot.adminStatus?.toLowerCase() || "pending";
      return selectedStatus === "all" || status === selectedStatus;
    });
  };

  // Get paginated products for a seller
  const getPaginatedPilotsForSeller = (sellerId) => {
    const filteredPilots = getFilteredPilotsForSeller(sellerId);
    const currentProductsPage = productsPage[sellerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;
   
    return {
      pilots: filteredPilots.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredPilots.length / PRODUCTS_PER_PAGE),
      totalPilots: filteredPilots.length
    };
  };

  // Handle products page change
  const handleProductsPageChange = (sellerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [sellerId]: page
    }));
  };

  // View pilot details
  const viewPilotDetails = (pilot) => {
    setViewingPilot(pilot);
  };

  // Close pilot details modal
  const closePilotDetails = () => {
    setViewingPilot(null);
  };

  // Handle seller page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  // Status statistics
  const statusStats = {
    all: pilots.length,
    approved: pilots.filter(p => p.adminStatus === "approved").length,
    pending: pilots.filter(p => !p.adminStatus || p.adminStatus === "pending").length,
    rejected: pilots.filter(p => p.adminStatus === "rejected").length,
    temporarily_deactivated: pilots.filter(p => p.adminStatus === "Temporarily_Deactivated").length,
  };

  // Overall stats
  const totalPilots = pilots.length;
  const availablePilots = pilots.filter(p => p.availability).length;
  const deactivatedPilots = pilots.filter(p => p.adminStatus === "Temporarily_Deactivated").length;

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const truncateText = (text, length = 30) => {
    if (!text) return "—";
    if (text.length <= length) return text;
    return text.substring(0, length) + "...";
  };

  const formatPrice = (price) => {
    if (!price) return "—";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Export function
  const handleExport = () => {
    const headers = ["Pilot ID", "Seller ID", "Name", "Company", "Location", "Availability", "Price/hr", "Status", "Specification"];
    const rows = pilots.map(p => [
      p.pilotId,
      p.sellerId,
      p.pilotName,
      p.pilotCompany || "—",
      p.location || "N/A",
      p.availability ? "Available" : "Unavailable",
      p.price?.perHour ? `₹${p.price.perHour}` : "—",
      p.adminStatus || "pending",
      truncateText(p.specification || "", 50)
    ]);
    exportToCSV(headers, rows, `hire_pilots_export_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Render content buttons for specification and description
  const renderContentButtons = (pilot) => {
    const hasSpecification = pilot.specification && pilot.specification.trim().length > 0;
    const hasDescription = pilot.description && pilot.description.trim().length > 0;
    const hasCertificates = pilot.certifications && pilot.certifications.length > 0;
    const hasResume = pilot.resume && pilot.resume.url;

    return (
      <div className="content-buttons-container" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
        {hasSpecification && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('specification', pilot.specification, `Specification - ${pilot.pilotName}`);
            }}
            style={{
              background: '#e0f2fe',
              color: '#0369a1',
              border: '1px solid #bae6fd',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📋 Spec
          </button>
        )}
       
        {hasDescription && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('description', pilot.description, `Description - ${pilot.pilotName}`);
            }}
            style={{
              background: '#f0f9ff',
              color: '#0c4a6e',
              border: '1px solid #e0f2fe',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📝 Desc
          </button>
        )}
       
        {hasCertificates && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('certificates', pilot.certifications, `Certificates - ${pilot.pilotName}`);
            }}
            style={{
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fde68a',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📜 Certs ({pilot.certifications.length})
          </button>
        )}
       
        {hasResume && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('resume', pilot.resume, `Resume - ${pilot.pilotName}`);
            }}
            style={{
              background: '#dbeafe',
              color: '#1e40af',
              border: '1px solid #bfdbfe',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📄 Resume
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="drones-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Pilot Management System</h1>
            <p>Admin Dashboard • Manage Pilot Services • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleExport}
              disabled={loading || pilots.length === 0}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Compact Stats Dashboard */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Pilots</div>
              <div className="compact-stat-value">{totalPilots}</div>
            </div>
          </div>
         
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Available Pilots</div>
              <div className="compact-stat-value">{availablePilots}</div>
            </div>
          </div>
         
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Sellers</div>
              <div className="compact-stat-value">{sellerGroups.length}</div>
            </div>
          </div>
         
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{statusStats.pending}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Deactivated</div>
              <div className="compact-stat-value">{deactivatedPilots}</div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Pilot Services Management</h2>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by seller ID, email, pilot name, company, or location..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
             
            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected", "temporarily_deactivated"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    setProductsPage({});
                  }}
                >
                  {status === "temporarily_deactivated" ? "Deactivated" : status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Sellers Table Container */}
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Sellers ({sellerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedSellers.length} sellers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading pilot data...</p>
                </div>
              ) : sellerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No sellers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("sellerId")}>
                        Seller ID <SortIcon column="sellerId" />
                      </th>
                      <th onClick={() => handleSort("email")}>
                        Contact Info <SortIcon column="email" />
                      </th>
                      <th onClick={() => handleSort("totalPilots")}>
                        Pilots <SortIcon column="totalPilots" />
                      </th>
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSellers.map((seller) => {
                      const { pilots: sellerPilots, currentPage: pilotsCurrentPage, totalPages: pilotsTotalPages, totalPilots } = getPaginatedPilotsForSeller(seller.sellerId);
                     
                      return (
                        <React.Fragment key={seller.sellerId}>
                          <tr
                            className={`seller-row ${expandedSeller === seller.sellerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedSeller(
                              expandedSeller === seller.sellerId ? null : seller.sellerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedSeller === seller.sellerId ? '▼' : '▶'}
                                </span>
                                <strong>Seller #{seller.sellerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  📧 {seller.sellerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {seller.sellerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{seller.totalPilots}</span>
                                <span className="count-label">pilots</span>
                              </div>
                            </td>
                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${seller.approvedPilots} approved`}>
                                  {seller.approvedPilots}
                                </span>
                                <span className="status-dot pending" title={`${seller.pendingPilots} pending`}>
                                  {seller.pendingPilots}
                                </span>
                                <span className="status-dot rejected" title={`${seller.rejectedPilots} rejected`}>
                                  {seller.rejectedPilots}
                                </span>
                                <span className="status-dot deactivated" title={`${seller.deactivatedPilots} deactivated`}>
                                  {seller.deactivatedPilots}
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSeller(
                                    expandedSeller === seller.sellerId ? null : seller.sellerId
                                  );
                                }}
                                title="View Pilots"
                              >
                                {expandedSeller === seller.sellerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>
                         
                          {/* Expanded Pilots Row */}
                          {expandedSeller === seller.sellerId && (
                            <tr className="products-row">
                              <td colSpan="5">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Pilots from Seller #{seller.sellerId}</h4>
                                      <span className="products-count">
                                        {totalPilots} pilots • Showing {sellerPilots.length} on page {pilotsCurrentPage} of {pilotsTotalPages}
                                      </span>
                                    </div>
                                  </div>
                                 
                                  {totalPilots === 0 ? (
                                    <div className="no-products">
                                      <p>No pilots found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Pilot ID</th>
                                              <th>Pilot Details</th>
                                              <th>Company</th>
                                              <th>Location</th>
                                              <th>Price</th>
                                              <th>Availability</th>
                                              <th>Status</th>
                                              <th>Content</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sellerPilots.map((pilot) => (
                                              <tr key={pilot.pilotId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {pilot.pilotId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    <div>
                                                      <div className="drone-name">{pilot.pilotName}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{pilot.pilotCompany || "—"}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{pilot.pilotCompany || "—"}</div>
                                                </td>
                                                <td>
                                                  <div className="drone-uin">{pilot.location || "N/A"}</div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    {formatPrice(pilot.price?.perHour)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className={`quantity-cell ${!pilot.availability ? 'out-of-stock' : ''}`}>
                                                    {pilot.availability ? "Available" : "Unavailable"}
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className={`status-badge status-${pilot.adminStatus === 'Temporarily_Deactivated' ? 'deactivated' : (pilot.adminStatus || "pending")}`}>
                                                    {pilot.adminStatus === 'Temporarily_Deactivated' ? 'DEACTIVATED' : (pilot.adminStatus || "pending").toUpperCase()}
                                                  </span>
                                                </td>
                                                <td style={{ maxWidth: '200px', minWidth: '150px' }}>
                                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                                      Click to view:
                                                    </div>
                                                    {renderContentButtons(pilot)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {/* For Pending Status */}
                                                    {(!pilot.adminStatus || pilot.adminStatus === "pending") && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "approved")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "rejected")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {/* For Approved Status */}
                                                    {pilot.adminStatus === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "pending")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "rejected")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {/* For Rejected Status */}
                                                    {pilot.adminStatus === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "pending")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "approved")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {/* For Deactivated Status - Only Approve, Pending, Reject options */}
                                                    {pilot.adminStatus === "Temporarily_Deactivated" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "pending")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "approved")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updatePilotStatus(pilot.pilotId, "rejected")}
                                                          disabled={updatingIds.has(`${pilot.pilotId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${pilot.pilotId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewPilotDetails(pilot)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>

                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deletePilot(pilot.pilotId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                     
                                      {/* Pilots Pagination */}
                                      {pilotsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, pilotsCurrentPage - 1)}
                                            disabled={pilotsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {pilotsCurrentPage} of {pilotsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, pilotsCurrentPage + 1)}
                                            disabled={pilotsCurrentPage === pilotsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls for Sellers */}
            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pilot Details Modal */}
      {viewingPilot && (
        <div className="modal-overlay" onClick={closePilotDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Pilot Details</h2>
              <button className="close-btn" onClick={closePilotDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-info-section">
                  <div className="info-row">
                    <div className="info-label">Pilot ID</div>
                    <div className="info-value">{viewingPilot.pilotId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{viewingPilot.pilotName}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Company</div>
                    <div className="info-value">{viewingPilot.pilotCompany || "—"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Location</div>
                    <div className="info-value">{viewingPilot.location || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Availability</div>
                    <div className="info-value">
                      <span className={`quantity-display ${!viewingPilot.availability ? 'out-of-stock' : ''}`}>
                        {viewingPilot.availability ? "Available" : "Unavailable"}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price (Per Hour)</div>
                    <div className="info-value price-value">{formatPrice(viewingPilot.price?.perHour)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price (Per Day)</div>
                    <div className="info-value price-value">{formatPrice(viewingPilot.price?.perDay)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingPilot.adminStatus === 'Temporarily_Deactivated' ? 'deactivated' : (viewingPilot.adminStatus || "pending")}`}>
                        {viewingPilot.adminStatus === 'Temporarily_Deactivated' ? 'DEACTIVATED' : (viewingPilot.adminStatus || "pending").toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller ID</div>
                    <div className="info-value">{viewingPilot.sellerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Email</div>
                    <div className="info-value">{viewingPilot.seller?.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Phone</div>
                    <div className="info-value">{viewingPilot.seller?.phoneNumber || "N/A"}</div>
                  </div>
                 
                  {/* Content buttons in modal */}
                  <div className="info-row full-width">
                    <div className="info-label">Content</div>
                    <div className="info-value" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {viewingPilot.specification && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('specification', viewingPilot.specification, `Specification - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📋 View Specification
                        </button>
                      )}
                     
                      {viewingPilot.description && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('description', viewingPilot.description, `Description - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#f0f9ff',
                            color: '#0c4a6e',
                            border: '1px solid #e0f2fe',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📝 View Description
                        </button>
                      )}
                     
                      {viewingPilot.certifications?.length > 0 && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('certificates', viewingPilot.certifications, `Certificates - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fde68a',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📜 View Certificates ({viewingPilot.certifications.length})
                        </button>
                      )}
                     
                      {viewingPilot.resume?.url && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('resume', viewingPilot.resume, `Resume - ${viewingPilot.pilotName}`)}
                          style={{
                            background: '#dbeafe',
                            color: '#1e40af',
                            border: '1px solid #bfdbfe',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📄 View Resume
                        </button>
                      )}
                    </div>
                  </div>
                </div>
               
                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {(!viewingPilot.adminStatus || viewingPilot.adminStatus === "pending") && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "approved");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "rejected");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "Temporarily_Deactivated");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingPilot.adminStatus === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "pending");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "rejected");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "Temporarily_Deactivated");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingPilot.adminStatus === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "pending");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "approved");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "Temporarily_Deactivated");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingPilot.adminStatus === "Temporarily_Deactivated" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "pending");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "approved");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updatePilotStatus(viewingPilot.pilotId, "rejected");
                            closePilotDetails();
                          }}
                          disabled={updatingIds.has(`${viewingPilot.pilotId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingPilot.pilotId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this pilot?")) {
                          deletePilot(viewingPilot.pilotId);
                          closePilotDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content View Modal */}
      {viewingContent.type && (
        <div className="modal-overlay" onClick={closeContent}>
          <div className="modal-content content-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh' }}>
            <div className="modal-header">
              <h2>{viewingContent.title}</h2>
              <button className="close-btn" onClick={closeContent}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {viewingContent.type === 'specification' && (
                <div className="specification-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Specification Details</h3>
                  <div className="content-text" style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    {viewingContent.content || 'No specification provided.'}
                  </div>
                </div>
              )}
             
              {viewingContent.type === 'description' && (
                <div className="description-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Description Details</h3>
                  <div className="content-text" style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    {viewingContent.content || 'No description provided.'}
                  </div>
                </div>
              )}
             
              {viewingContent.type === 'certificates' && (
                <div className="certificates-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Certificates</h3>
                  <div className="certificates-list" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {viewingContent.content.length === 0 ? (
                      <p style={{ color: '#6b7280', textAlign: 'center' }}>No certificates uploaded.</p>
                    ) : (
                      viewingContent.content.map((cert, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '16px',
                          backgroundColor: '#f0f9ff',
                          borderRadius: '8px',
                          border: '1px solid #bae6fd'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              backgroundColor: '#0ea5e9',
                              color: 'white',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px'
                            }}>
                              📄
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: '#0369a1' }}>
                                Certificate {index + 1}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                Click to view/download
                              </div>
                            </div>
                          </div>
                          <a
                            href={cert.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontWeight: '500',
                              fontSize: '14px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                          >
                            View Certificate
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
             
              {viewingContent.type === 'resume' && (
                <div className="resume-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Resume</h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px'
                    }}>
                      📄
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', fontSize: '16px', color: '#1f2937', marginBottom: '4px' }}>
                        Professional Resume
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        Click the button below to view/download the resume
                      </div>
                    </div>
                    <a
                      href={viewingContent.content.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '16px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                      <span>📄</span>
                      <span>View Resume</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HirePilotsDashboard;